/** Step 1 — 心情选择 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSettings } from '../../components/providers/AppSettingsProvider';
import { loadAuthUser } from '../../services/auth';
import { createPageUrl } from '../../utils';
import { CheckInShell } from './components/CheckInShell';
import { CHECK_IN_EMOTIONS, moodPillVariantClass } from './data/moods';

function subtitleKeyForHour(hour: number): string {
  if (hour < 12) return 'checkIn.subtitleMorning';
  if (hour < 18) return 'checkIn.subtitleAfternoon';
  return 'checkIn.subtitleEvening';
}

const CheckIn: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useAppSettings();
  const [selected, setSelected] = useState<string | null>(null);

  const subtitleKey = useMemo(() => subtitleKeyForHour(new Date().getHours()), []);

  useEffect(() => {
    if (!loadAuthUser()) {
      navigate('/splash', { replace: true, state: { forceLogin: true } });
    }
  }, [navigate]);

  const tagLabel = useCallback((tag: string) => t(`checkIn.tags.${tag}`) || tag, [t]);

  const handleContinue = () => {
    if (!selected) return;
    navigate(createPageUrl('CheckInEvent'), { state: { moodTag: selected } });
  };

  return (
    <CheckInShell
      hideBack
      ctaLabel={t('checkIn.continue')}
      onCta={handleContinue}
      ctaDisabled={!selected}
      bodyPb="pb-36"
    >
      <div className="mt-6 text-center">
        <p className="text-lg font-medium tracking-tight text-white">
          {t('checkIn.greeting')}
        </p>
        <p className="mt-2 text-base font-normal leading-snug text-white/90">
          {t(subtitleKey)}
        </p>
      </div>

      <div className="mt-7 w-full max-w-full">
        <p className="mb-3 text-center text-[11px] font-medium text-white/45">
          {t('checkIn.swipeHint')}
        </p>
        <div
          className="-mx-4 -my-2 overflow-x-auto overscroll-x-contain px-4 py-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label={t('checkIn.moodPicker')}
        >
          <div className="mx-auto grid w-max grid-cols-6 gap-x-2.5 gap-y-3" role="list">
            {CHECK_IN_EMOTIONS.map(({ tag, variant }) => {
              const on = selected === tag;
              return (
                <button
                  key={tag}
                  type="button"
                  role="listitem"
                  onClick={() => setSelected(tag)}
                  className={`w-[5.75rem] shrink-0 rounded-full px-2 py-2.5 text-center text-[12px] font-medium leading-tight transition-transform duration-200 sm:w-[6rem] sm:text-[13px] ${moodPillVariantClass[variant]} ${
                    on
                      ? 'scale-[1.04] ring-2 ring-white/90 ring-offset-2 ring-offset-[#0a1224]'
                      : 'opacity-95 hover:opacity-100 active:scale-[0.98]'
                  }`}
                >
                  {tagLabel(tag)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </CheckInShell>
  );
};

export default CheckIn;
